/**
 * #1018 — one plain-words vocabulary for Polymarket US reason codes.
 *
 * Every surface that shows a US refusal, an unconfirmed result or an
 * unavailable account reads it from here, so the same code never reads two
 * ways. The visible text is the plain reason; callers keep the raw code in the
 * element's `title`. Unknown codes stay visible, wrapped rather than bare.
 *
 * Two forms:
 * - `usReason` / `usReasonStatus`: the account vocabulary ("View only:
 *   <reason>" and a short badge), shared by the Game Detail ticket and Settings.
 * - `usOrderReason` / `usOrderReasonSentence`: why an order, a cancel or an
 *   order read was refused or is unconfirmed ("Not sent: <reason>.").
 */
import { formatUsDecimal } from './format';
import type { UsLimitBreach } from './types';

type Entry = {
  /** Short badge label. */
  status: string;
  /** Sentence fragment, no final period: "View only: <detail>", "Not sent: <detail>.". */
  detail: string;
  /** The order/cancel wording, where the account wording would mislead. */
  order?: string;
  /** Placing the same order again will be refused the same way. */
  repeats?: true;
};

const UNCHECKED = 'couldn’t be checked';
const RECHECK = 'place it again to re-check it';

const REASON: Record<string, Entry> = {
  // ── Account and venue availability (#1018; Settings pins this wording) ──
  US_RUNTIME_UNAVAILABLE: { status: 'Service not running', detail: 'the Polymarket US trading service is not running' },
  US_EXECUTION_NOT_ACTIVATED: { status: 'Not activated', detail: 'this account is not activated for trading' },
  US_NOT_READY: { status: 'Not ready', detail: 'this account is not ready to trade' },
  US_CATALOGUE_UNAVAILABLE: { status: 'Market data unavailable', detail: 'Polymarket US market data is unavailable' },
  US_MARKET_UNAVAILABLE: { status: 'Market data unavailable', detail: 'Polymarket US market data is unavailable' },
  US_READ_UNAVAILABLE: { status: 'Unreadable', detail: 'Polymarket US accounts could not be read', order: 'the order couldn’t be read' },
  US_CREDENTIAL_DISABLED: { status: 'Key disabled', detail: 'this account’s key is disabled; ask a manager' },
  US_EXECUTION_READINESS_NOT_PROVIDED: { status: 'Readiness unknown', detail: 'this account’s trading readiness wasn’t provided' },

  // ── Limits and permissions: the same order is refused again ──
  US_RISK_LIMIT: { status: 'Over limit', detail: 'over your limit; placing again will be refused until the limit changes', repeats: true },
  US_SELL_EXCEEDS_HOLDING: { status: 'Exceeds holding',
    detail: 'sell exceeds what this account holds after resting sells; Polymarket US can’t sell short', repeats: true },
  US_SPORT_NOT_ALLOWED: { status: 'Sport not allowed', detail: 'this sport is not in your account’s allowed list', repeats: true },
  US_ACTION_FORBIDDEN: { status: 'Not permitted', detail: 'your role does not allow this action on this account', repeats: true },
  US_INSUFFICIENT_FUNDS: { status: 'Insufficient buying power', detail: 'insufficient buying power' },
  US_VENUE_REJECTED: { status: 'Rejected', detail: 'rejected by the venue' },

  // ── Account state the relay must reconcile before an order ──
  US_OUTSIDE_ACTIVITY_UNRECONCILED: { status: 'Reconciling',
    detail: 'this account has activity from outside G1 that is not reconciled yet; new orders wait until it is' },
  US_OUTSIDE_OPEN_ORDER_UNRECONCILED: { status: 'Reconciling',
    detail: 'this account has an open order from outside G1 that is not reconciled yet; new orders wait until it is' },
  US_OPEN_ORDERS_UNAVAILABLE: { status: 'Unchecked', detail: `this account’s open orders ${UNCHECKED}` },
  US_SELL_INVENTORY_UNAVAILABLE: { status: 'Unchecked', detail: `this account’s holding ${UNCHECKED}, so a sell can’t be sent` },
  US_EXPOSURE_UNAVAILABLE: { status: 'Unchecked', detail: `your current exposure ${UNCHECKED} against your limits` },
  US_SHARED_LIMITS_UNAVAILABLE: { status: 'Unchecked', detail: `your current exposure ${UNCHECKED} against your limits` },
  US_CAP_EXPOSURE_UNREADABLE: { status: 'Unchecked', detail: `your current exposure ${UNCHECKED} against your limits` },
  US_RISK_OWNERSHIP_CHANGED: { status: 'Unchecked', detail: 'this account’s holdings changed hands while the order was checked' },
  US_RISK_OWNERSHIP_UNAVAILABLE: { status: 'Unchecked', detail: `this account’s ownership ${UNCHECKED} for your limits` },
  US_ACCOUNT_COMPARISON_UNAVAILABLE: { status: 'Reconciling', detail: 'this account’s holdings don’t reconcile with G1’s records yet' },
  US_ACCOUNT_NET_UNRECONCILED: { status: 'Reconciling', detail: 'this account’s holdings don’t reconcile with G1’s records yet' },

  // ── The check went stale before the order was sent ──
  US_SCOPE_CHANGED: { status: 'Account changed', detail: 'the account changed while the order was checked; review it and place again' },
  US_PREVIEW_CONSUMED: { status: 'Check used', detail: `this check was already used; ${RECHECK}` },
  US_PREVIEW_EXPIRED: { status: 'Check expired', detail: `the check expired before the order went out; ${RECHECK}` },
  US_JOURNAL_EVIDENCE_EXPIRED: { status: 'Check expired', detail: `market or fee data expired before the order went out; ${RECHECK}` },
  US_EVIDENCE_EXPIRED: { status: 'Check expired', detail: `market or fee data expired before the order went out; ${RECHECK}` },
  US_FEE_EVIDENCE_EXPIRED: { status: 'Check expired', detail: `market or fee data expired before the order went out; ${RECHECK}` },
  US_PREVIEW_CHANGED: { status: 'Market changed', detail: 'the market changed after the check; review the ticket and place again' },
  US_MARKET_CHANGED: { status: 'Market changed', detail: 'the market changed after the check; review the ticket and place again' },
  US_CATALOGUE_CHANGED: { status: 'Market changed', detail: 'the market changed after the check; review the ticket and place again' },
  US_FEE_PREVIEW_CHANGED: { status: 'Market changed', detail: 'the market’s fees changed after the check; review the ticket and place again' },
  US_DISPATCH_EVIDENCE_CHANGED: { status: 'Market changed', detail: 'the market changed after the check; review the ticket and place again' },
  US_IDEMPOTENCY_CONFLICT: { status: 'Request conflict', detail: 'this saved request already belongs to a different order' },

  // ── The ticket's entry, as the relay read it ──
  US_INVALID_REQUEST: { status: 'Invalid request', detail: 'the relay could not read this request' },
  US_INVALID_DRAFT: { status: 'Invalid request', detail: 'the relay could not read this order' },
  US_UNEXPECTED_DRAFT_FIELD: { status: 'Invalid request', detail: 'the relay could not read this order' },
  US_INVALID_IDENTITY: { status: 'Invalid request', detail: 'the relay could not read this order’s market' },
  US_INVALID_ACTION: { status: 'Invalid request', detail: 'the side (buy or sell) is not valid' },
  US_INVALID_OUTCOME: { status: 'Invalid request', detail: 'the outcome is not valid' },
  US_INVALID_DECIMAL: { status: 'Invalid size', detail: 'the size or price is not a plain number' },
  US_INVALID_QUANTITY: { status: 'Invalid size', detail: 'the number of contracts is outside this market’s minimum, step or maximum' },
  US_QUANTITY_PRECISION: { status: 'Invalid size', detail: 'the number of contracts has more decimals than this market allows' },
  US_INVALID_PRICE: { status: 'Invalid price', detail: 'the price must be between $0.01 and $0.99' },
  US_PRICE_OFF_TICK: { status: 'Invalid price', detail: 'the price is not on this market’s tick' },
  US_PRICE_OUT_OF_BAND: { status: 'Invalid price', detail: 'the price is outside the band this market accepts now' },
  US_EXPIRY_REQUIRED: { status: 'Invalid expiry', detail: 'good till date needs an expiry' },
  US_INVALID_EXPIRY: { status: 'Invalid expiry', detail: 'the expiry must be a future UTC time' },
  US_UNEXPECTED_EXPIRY: { status: 'Invalid expiry', detail: 'only good till date takes an expiry' },
  US_UNSUPPORTED_DURATION: { status: 'Invalid duration', detail: 'this market does not offer that duration' },
  US_POST_ONLY_REQUIRED: { status: 'Invalid post-only', detail: 'post-only must be set on or off' },
  US_POST_ONLY_UNSUPPORTED: { status: 'Invalid post-only', detail: 'post-only is not available for this market or duration' },
  US_RULES_UNAVAILABLE: { status: 'Rules unavailable', detail: 'this market’s order rules couldn’t be read' },
  US_MARKET_RULES_UNAVAILABLE: { status: 'Rules unavailable', detail: 'this market’s order rules couldn’t be read' },
  US_MARKET_UNSUPPORTED: { status: 'Unsupported market', detail: 'this market is not supported here' },

  // ── Relay and transport ──
  US_REQUEST_NOT_FOUND: { status: 'Not found', detail: 'the relay has no record of this request' },
  US_REQUEST_UNCONFIRMED: { status: 'Unconfirmed', detail: 'the relay couldn’t confirm the outcome' },
  US_SUBMISSION_UNCONFIRMED: { status: 'Unconfirmed', detail: 'the venue’s answer to the order was lost' },
  US_ACCEPTANCE_UNCONFIRMED: { status: 'Unconfirmed', detail: 'the venue’s acceptance isn’t confirmed yet' },
  US_TRANSPORT_TIMEOUT: { status: 'Timed out', detail: 'the venue did not answer in time' },
  US_REQUEST_TIMEOUT: { status: 'Timed out', detail: 'the venue did not answer in time' },
  US_TIMEOUT: { status: 'Timed out', detail: 'the venue did not answer in time' },
  US_PROCESS_RESTART: { status: 'Interrupted', detail: 'G1 restarted while the order was in progress' },
  US_REASON_UNAVAILABLE: { status: 'No reason', detail: 'no reason was recorded' },
  US_ORDER_NOT_SENT: { status: 'Not sent', detail: 'G1 recorded the order but did not send it' },
  US_AUTH_REQUIRED: { status: 'Signed out', detail: 'your session ended; sign in again' },
  US_REQUEST_TOO_LARGE: { status: 'Invalid request', detail: 'the request was too large' },
  RELAY_DRAINING: { status: 'Restarting', detail: 'G1 is restarting; nothing was sent' },

  // ── This browser's trading client (nothing left the browser) ──
  US_CLIENT_PREVIEW_REQUIRED: { status: 'Check expired', detail: `the check expired before the order went out; ${RECHECK}` },
  US_CLIENT_DRAFT_REQUIRED: { status: 'No order', detail: 'the ticket had no order to send' },
  US_CLIENT_CONFIRMATION_CONSUMED: { status: 'Check used', detail: `this check was already used; ${RECHECK}` },
  US_CLIENT_RECOVERY_QUEUE_FULL: { status: 'Requests waiting',
    detail: '100 unchecked US requests are saved in this browser. Check and dismiss them under Your saved US requests', repeats: true },
  US_CLIENT_REQUEST_KEY_REUSED: { status: 'Browser error', detail: 'this browser couldn’t create a new request id' },
  US_CLIENT_INVALID_REQUEST_KEY: { status: 'Browser error', detail: 'this browser couldn’t create a new request id' },
  US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE: { status: 'Browser storage unavailable',
    detail: 'this browser’s storage is unavailable, so the request couldn’t be saved for recovery' },
  US_CLIENT_RECOVERY_STORAGE_INVALID: { status: 'Browser storage unreadable',
    detail: 'saved US requests in this browser couldn’t be read' },
  US_CLIENT_AUTH_REQUIRED: { status: 'Signed out', detail: 'your session ended; sign in again' },
  US_CLIENT_ACCOUNT_REQUIRED: { status: 'No account', detail: 'no account is selected' },
  US_CLIENT_CONTEXT_CHANGED: { status: 'Context changed', detail: 'your login or account changed while the request was out' },
  US_CLIENT_INVALID_RESPONSE: { status: 'Unreadable', detail: 'the answer couldn’t be read' },
  US_INVALID_RESPONSE: { status: 'Unreadable', detail: 'the answer couldn’t be read' },
  US_CLIENT_INVALID_SCOPE: { status: 'Account unavailable', detail: 'the ticket couldn’t bind to this account' },
  US_CLIENT_INVALID_REQUEST: { status: 'Invalid request', detail: 'the request was incomplete' },
  US_CLIENT_ORDER_CHANGED: { status: 'Order changed', detail: 'the order changed since it was shown' },
  US_CLIENT_CANCEL_PENDING: { status: 'Cancel pending', detail: 'another cancel is still in progress' },
  US_CLIENT_RECEIPT_REQUIRED: { status: 'No receipt', detail: 'no receipt has been seen for this request' },
  US_CLIENT_REQUEST_NOT_FOUND: { status: 'Not found', detail: 'this saved request is not in this browser' },

  // ── The ticket's own steps ──
  US_PREVIEW_UNAVAILABLE: { status: 'Unchecked', detail: 'the relay couldn’t check the order' },
  US_SUBMIT_UNAVAILABLE: { status: 'Unconfirmed', detail: 'the order request’s answer couldn’t be read' },
  US_PREVIEW_MISMATCH: { status: 'Check mismatch', detail: 'the relay’s check didn’t match this ticket' },
  US_ACCESS_CHANGED: { status: 'Access changed', detail: 'your access to this account changed' },
  US_ACCESS_DENIED: { status: 'Access ended', detail: 'your access to this account ended; pick an account again or ask a manager', repeats: true },

  // ── An edit's cancel and read-back ──
  US_ORDER_OPEN: { status: 'Still open', detail: 'the original is still open; the cancel hasn’t taken effect' },
  US_ORDER_PARTIAL: { status: 'Still open', detail: 'the original is still open (partly filled); the cancel hasn’t taken effect' },
  US_ORDER_PENDING: { status: 'Awaiting acceptance', detail: 'the original is still awaiting acceptance; the cancel hasn’t taken effect' },
  US_ORDER_REPLACED: { status: 'Reconciling', detail: 'the original is being reconciled at the venue' },
  US_ORDER_REJECTED: { status: 'Rejected', detail: 'the venue reports the original as rejected' },
  US_ORDER_CONFLICT: { status: 'Conflicting', detail: 'the venue’s evidence for the original conflicts' },
  US_ORDER_NOT_OBSERVED: { status: 'Not seen', detail: 'the original hasn’t been seen at the venue yet' },
  US_ORDER_FILLS_UNREADABLE: { status: 'Fills unconfirmed', detail: 'the original’s fills couldn’t be confirmed' },
  US_ORDER_FILLS_REGRESSED: { status: 'Fills unconfirmed', detail: 'the original’s fills couldn’t be confirmed' },
  US_CANCEL_TARGET_MISMATCH: { status: 'Unconfirmed', detail: 'the cancel’s answer was for a different order' },
  US_CANCEL_NOT_SENT: { status: 'Not sent', detail: 'the relay proved the cancel was not sent' },
  US_CANCEL_READBACK_UNAVAILABLE: { status: 'Unconfirmed', detail: 'the cancel went out, but the order couldn’t be read back' },
  US_CANCEL_UNCONFIRMED: { status: 'Unconfirmed', detail: 'the cancel’s answer was lost' },
  US_CANCEL_UNAVAILABLE: { status: 'Unavailable', detail: 'cancels are unavailable for this account right now' },
  US_KNOWN_ORDER_REQUIRED: { status: 'Not at venue', detail: 'the order has no venue id yet' },
  US_CANCEL_RECOVERY_WAIT: { status: 'Wait', detail: 'a repeat cancel must wait 10 seconds after the first' },
};

/** Sentence fragment for the account vocabulary, e.g. "View only: <reason>". */
export const usReason = (code: string) => REASON[code]?.detail ?? `Polymarket US is unavailable (${code})`;
/** #1238 — the venue-down line in the K/P words ("Kalshi account unavailable."):
 * "Polymarket US unavailable: <reason>." for a known code, the code in
 * parentheses otherwise. */
export const usUnavailableLine = (code: string) => {
  const detail = REASON[code]?.detail;
  // The line already names the venue: a detail that starts with it ("Polymarket
  // US market data…", "the Polymarket US trading service…") drops it.
  return detail ? `Polymarket US unavailable: ${detail.replace(/^(the )?Polymarket US /, '$1')}.` : `Polymarket US unavailable (${code}).`;
};
/** Every code the reason vocabulary words (read-only; for tests that sweep it). */
export const US_REASON_CODES: readonly string[] = Object.freeze(Object.keys(REASON));
/** Short badge label; the full `usReason` belongs in its tooltip. */
export const usReasonStatus = (code: string) => REASON[code]?.status ?? 'Unavailable';

const count = (n: number) => formatUsDecimal(String(n));

function limitReason(limit: UsLimitBreach): string {
  const rule = limit.rule === 'LIMIT_MAX_POSITIONS' ? 'position' : 'contract';
  const total = limit.current + limit.requested;
  // Whole counts only: a fractional sum is not added up in floating point here.
  const figures = [limit.cap, limit.current, limit.requested].every(Number.isSafeInteger) && Number.isSafeInteger(total)
    ? ` (${count(total)} > ${count(limit.cap)})` : ` of ${count(limit.cap)}`;
  return `over your ${rule} cap${figures}; placing again will be refused until the limit changes`;
}

/**
 * Why an order, a cancel or an order read was refused or is unconfirmed, as
 * a fragment ("Not sent: <reason>."). A cap refusal names the cap when the
 * limit check reported it. Unknown codes stay visible.
 */
export function usOrderReason(code: string, limit?: UsLimitBreach | null): string {
  if (code === 'US_RISK_LIMIT' && limit && Number.isSafeInteger(limit.cap)) return limitReason(limit);
  const entry = REASON[code];
  return entry ? entry.order ?? entry.detail : `Polymarket US code ${code}`;
}

/** `usOrderReason` as one sentence. */
export function usOrderReasonSentence(code: string, limit?: UsLimitBreach | null): string {
  const text = usOrderReason(code, limit);
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

/** True when placing the same order again will be refused the same way. */
export function usReasonRepeatRefused(code: string): boolean {
  return REASON[code]?.repeats === true;
}
